import React from "react";
import { Button } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  getTeamHotelImageCopyFile,
  getTeamHotelImageCopySourceFile,
  getTeamHotelImageFile,
} from "../services/leadTrackerApi";
import { roomImagesToSheet, roomImageWithCompactHeader, videoFileExtension } from "../pages/LeadTeamPortal";

const canCopyImages = () => Boolean(window.ClipboardItem && navigator.clipboard?.write);

/**
 * Call / Chat team only: copy a chosen stay's room photos (all as one sheet, or one at a time) and
 * download its room videos, straight from the trip page. It renders only when a team login is in
 * this browser, and every file comes from the team's login-checked endpoints - a customer never has
 * that login, so neither the button nor the files reach them. Photos carry the same "Hotel: display
 * name" header as the Hotel Images page, never the real name.
 */
export default function TeamStayMedia({ team, token, hotelRow, categories = [], onExpired }) {
  const [showSingles, setShowSingles] = React.useState(false);
  const [busy, setBusy] = React.useState(null);
  const [selectedCategoryId, setSelectedCategoryId] = React.useState("");

  // Stay on the room type picked here while the stay's room types stay the same; otherwise start on
  // the first one the group is booked into (they are listed first).
  const category = categories.find((item) => item.categoryId === selectedCategoryId) || categories[0] || null;
  // Which room types exist, not their order - booked ones move to the front as rooms change, and
  // adjusting a room count must not jump the photos back to another room type.
  const categoryKey = categories.map((item) => item.categoryId).sort().join(",");
  React.useEffect(() => {
    setSelectedCategoryId("");
    setShowSingles(false);
  }, [categoryKey]);

  const pickCategory = (categoryId) => {
    setSelectedCategoryId(categoryId);
    setShowSingles(false);
  };

  const media = category?.images || [];
  const photos = media.filter((item) => item.mediaType !== "VIDEO");
  const videos = media.filter((item) => item.mediaType === "VIDEO");

  const failed = (error, message) => {
    if (error?.response?.status === 401 || error?.response?.status === 403) {
      onExpired?.();
      toast.error("Your team login has expired. Log in to the Call / Chat portal again.");
      return;
    }
    console.error(message, error);
    toast.error(message);
  };

  const copyAll = async () => {
    setBusy("all");
    try {
      if (!canCopyImages()) throw new Error("Image clipboard unsupported");
      const blobs = await Promise.all(photos.map(async (photo) => {
        try {
          return await getTeamHotelImageCopySourceFile(team, token, photo.id);
        } catch (error) {
          if (error?.response?.status === 401 || error?.response?.status === 403) throw error;
          return getTeamHotelImageFile(team, token, photo.id);
        }
      }));
      const sheet = await roomImagesToSheet(blobs, hotelRow, category);
      await navigator.clipboard.write([new ClipboardItem({ "image/png": sheet })]);
      toast.success(photos.length === 1 ? "Photo copied. Paste it in WhatsApp." : `${photos.length} photos copied as one image. Paste it in WhatsApp.`);
    } catch (error) {
      failed(error, "Could not copy the photos. Try copying them one by one.");
    } finally {
      setBusy(null);
    }
  };

  const copyOne = async (photo) => {
    setBusy(`photo-${photo.id}`);
    try {
      if (!canCopyImages()) throw new Error("Image clipboard unsupported");
      let blob;
      try {
        blob = await getTeamHotelImageCopyFile(team, token, photo.id);
      } catch (error) {
        if (error?.response?.status === 401 || error?.response?.status === 403) throw error;
        blob = await roomImageWithCompactHeader(await getTeamHotelImageFile(team, token, photo.id), hotelRow, category);
      }
      await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
      toast.success("Photo copied. Paste it in WhatsApp.");
    } catch (error) {
      failed(error, "Could not copy this photo.");
    } finally {
      setBusy(null);
    }
  };

  const downloadVideo = async (video, index) => {
    setBusy(`video-${video.id}`);
    try {
      const blob = await getTeamHotelImageFile(team, token, video.id);
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = `${hotelRow?.customerCode || "stay"}-${category?.categoryName || "room"}-video-${index + 1}.${videoFileExtension(blob.type, video.imageUrl)}`
        .replace(/[^\w.-]+/g, "-");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } catch (error) {
      failed(error, "Could not download this video.");
    } finally {
      setBusy(null);
    }
  };

  if (!category) return null;

  const roomTypePicker = categories.length > 1 && (
    <div className="d-flex flex-wrap gap-1 mb-2" role="group" aria-label="Room type photos">
      {categories.map((item) => {
        const active = item.categoryId === category.categoryId;
        const count = (item.images || []).length;
        return (
          <Button
            key={item.categoryId}
            type="button"
            size="sm"
            variant={active ? "primary" : "outline-primary"}
            className="team-stay-media__type"
            aria-pressed={active}
            onClick={() => pickCategory(item.categoryId)}
          >
            {item.categoryName}
            {item.inUse ? " ✓" : ""}
            <span className={active ? "" : "text-muted"}> ({count})</span>
          </Button>
        );
      })}
    </div>
  );

  const header = (
    <span className="small fw-semibold text-muted">
      <i className="bi bi-person-badge me-1" aria-hidden="true" />
      Team only · {category.categoryName}
      {category.inUse ? " (booked for this group)" : ""}
    </span>
  );

  if (!media.length) {
    return (
      <div className="team-stay-media">
        {roomTypePicker}
        <div className="small text-muted">
          {header}
          <div className="mt-1">
            <i className="bi bi-images me-1" aria-hidden="true" />
            No photos or videos uploaded for {category.categoryName} yet.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="team-stay-media">
      {roomTypePicker}
      <div className="d-flex flex-wrap align-items-center gap-2">
        {header}
        {photos.length > 0 && (
          <Button type="button" size="sm" variant="outline-primary" disabled={busy !== null} onClick={copyAll}>
            {busy === "all" ? <span className="spinner-border spinner-border-sm" aria-hidden="true" /> : `Copy all photos (${photos.length})`}
          </Button>
        )}
        {photos.length > 1 && (
          <Button type="button" size="sm" variant="link" className="p-0" onClick={() => setShowSingles((current) => !current)}>
            {showSingles ? "Hide single photos" : "Copy single photos"}
          </Button>
        )}
        {videos.map((video, index) => (
          <Button
            key={video.id}
            type="button"
            size="sm"
            variant="outline-secondary"
            disabled={busy !== null}
            onClick={() => downloadVideo(video, index)}
          >
            {busy === `video-${video.id}` ? (
              <span className="spinner-border spinner-border-sm" aria-hidden="true" />
            ) : (
              <><i className="bi bi-download me-1" aria-hidden="true" />Video {videos.length > 1 ? index + 1 : ""}</>
            )}
          </Button>
        ))}
      </div>
      {(showSingles || photos.length === 1) && photos.length > 0 && (
        <div className="d-flex flex-wrap gap-2 mt-2">
          {photos.map((photo, index) => (
            <div key={photo.id} className="team-stay-media__thumb">
              <img src={photo.imageUrl} alt={`${category?.categoryName || "Room"} ${index + 1}`} loading="lazy" />
              <Button
                type="button"
                size="sm"
                variant="light"
                className="w-100"
                disabled={busy !== null}
                onClick={() => copyOne(photo)}
              >
                {busy === `photo-${photo.id}` ? <span className="spinner-border spinner-border-sm" aria-hidden="true" /> : "Copy"}
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
